import React, { useState, useEffect, Suspense, lazy } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { openModal } from '../../../../redux/actions/modals';
import { Formik } from 'formik';
import MaskedInput from 'react-text-mask';
import {
	checkEmailAvailability,
	registerUser,
	setEmailTaken,
} from '../../../../redux/actions/auth';
import {
	book,
	setBookContact,
	cancelPaypal,
	setCardErrors,
} from '../../../../redux/actions/book';
import { updateMovePlan } from '../../../../redux/actions/movePlan';
import has from 'lodash-es/has';
import ReactLoading from 'react-loading';
import { getTotalCost } from '../../../../redux/reducers/book';

const AsyncBraintreeCardFields = lazy(() => import('./BraintreeCardFields'));

const mapStateToProps = ({ analytics, movePlan, auth, book }) => ({
	isBooked: movePlan.currentPlan.is_booked,
	isLoggedIn: has(auth, 'user'),
	emailTaken: auth.isEmailTaken,
	mpid: movePlan.currentMPID,
	paypalComplete: has(book, 'paypalComplete') ? book.paypalComplete : false,
	paypalEmail: !!book.paypalEmail ? book.paypalEmail : '',
	cardErrors: book.cardErrors,
	loading: book.loading,
	chargeDate: movePlan.validDates.card_charge_date,
	totalPrice: has(movePlan.currentPlan, 'pricing')
		? getTotalCost(movePlan, analytics)
		: undefined,
	braintreeFieldsTouched: book.braintreeFieldsTouched,
	showHourly: movePlan.currentPlan.hourly_plan,
});

const BookForm = props => {
	const [paymentType, setPaymentType] = useState('card');

	useEffect(() => {
		if (props.paypalComplete) setPaymentType('paypal');
	}, [props.paypalComplete]);

	useEffect(() => {
		return () => {
			props.setEmailTaken(false);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleEmailBlur = values => {
		if (values.registrationEmail.trim() === '') {
			props.setEmailTaken(false);
			return;
		}
		props.checkEmailAvailability(values.registrationEmail.toLowerCase());
	};
	const handlePaypalCancel = () => {
		setPaymentType('card');
		props.cancelPaypal();
	};
	return (
		<>
			<Formik
				initialValues={{
					name: '',
					phoneNumber: '',
					agree: false,
					user_note: '',
					registrationEmail: '',
					newPassword1: '',
					newPassword2: '',
					usingPaypal: false,
					fname: '',
					lname: '',
				}}
				validateOnChange={false}
				validateOnBlur={false}
				validate={values => {
					let errors = {};
					if (!!!values.name) {
						errors.name = 'Name is required';
					} else if (!/^[a-zA-Z-\s']+$/i.test(values.name)) {
						errors.name =
							'Name may only contain letters, dashes, or apostrophes.';
					}
					if (!!!values.phoneNumber) {
						errors.phoneNumber = 'Phone number is required';
					} else if (
						!/^\D?(\d{3})\D?\D?(\d{3})\D?(\d{4})$/i.test(values.phoneNumber)
					) {
						errors.phoneNumber = 'Phone number is invalid.';
					}

					if (paymentType === 'card') {
						if (!values.fname) {
							errors.fname = true;
						}
						if (!!!values.lname) {
							errors.lname = true;
						}
					}

					if (!!!values.agree) {
						errors.agree = true;
					}

					if (!props.isLoggedIn) {
						if (!!!values.registrationEmail) {
							errors.registrationEmail = 'Email is required';
						}
						if (
							!/^[\w+\-.]+@[a-z\d]+(\.[a-z]+)*\.[a-z]+$/i.test(
								values.registrationEmail
							)
						) {
							errors.registrationEmail = 'Invalid email';
						}
						if (!(!!values.newPassword1 && !!values.newPassword2)) {
							errors.newPassword1 = 'Password is required';
						}
						if (values.newPassword1.length < 6) {
							errors.newPassword1 =
								'Password should be at least 6 characters long';
						}
						if (values.newPassword2 !== values.newPassword1) {
							errors.newPassword2 = 'Passwords do not match';
						}
					}

					return errors;
				}}
				handleSubmit={e => e.preventDefault()}
				onSubmit={values => {
					if (props.emailTaken) return;

					if (paymentType === 'card') {
						if (
							Object.keys(props.cardErrors).some(
								key => props.cardErrors[key] === null
							)
						) {
							// case where one of the braintree fields was never touched
							let tmp = props.cardErrors;
							Object.keys(props.cardErrors).forEach(errKey =>
								props.cardErrors[errKey] === null
									? (tmp[errKey] = 'Required')
									: ''
							);
							props.setCardErrors(tmp);
							return;
						}
						//check if there are existing hosted fields errors from braintree
						let cardHasErrors = Object.keys(props.cardErrors).some(key => {
							return !!props.cardErrors[key];
						});
						if (cardHasErrors) return;
					}
					props.book(values);
				}}
				render={({
					values,
					errors,
					touched,
					handleChange,
					handleBlur,
					handleSubmit,
					setFieldError,
				}) => (
					<form
						onSubmit={e => handleSubmit(e)}
						className="billing-form"
						name="billing-form"
						id="book-form"
						noValidate>
						<fieldset className="fields contact-info ">
							<button
								type="submit"
								disabled={props.loading}
								className="primary-btn-large uppercase book abs-position">
								{props.loading ? (
									<ReactLoading
										className="m-auto ladda"
										type={'spokes'}
										color={'#ffffff'}
										height={27}
										width={27}
									/>
								) : props.isBooked ? (
									'Update'
								) : (
									'Book'
								)}
							</button>
							<div className=" container">
								<h3 className="fields-title col-sm-12 pl-0">
									Contact Information
								</h3>
								<div className="row">
									<div className="field name col-sm-12 col-md-6">
										<input
											onChange={handleChange}
											onFocus={() => setFieldError('name')}
											onBlur={handleBlur}
											type="text"
											name="name"
											className={`unpakt-textbox ${
												errors.name !== undefined &&
												touched.name &&
												'field-error'
											}`}
											placeholder="Name"
											value={values.name}
											required
										/>
										{errors.name !== undefined && touched.name && (
											<span className="field-error-message message">
												{errors.name}
											</span>
										)}
									</div>
									<div className="field phone col-sm-12 col-md-6">
										<MaskedInput
											value={values.phoneNumber}
											onChange={handleChange}
											onFocus={() => setFieldError('phoneNumber')}
											onBlur={handleBlur}
											type="text"
											name="phoneNumber"
											className={`unpakt-textbox w-100 m-0 ${
												errors.phoneNumber !== undefined &&
												touched.phoneNumber &&
												'field-error'
											}`}
											placeholder="Phone Number"
											required
											mask={[
												'(',
												/[1-9]/,
												/\d/,
												/\d/,
												')',
												' ',
												/\d/,
												/\d/,
												/\d/,
												'-',
												/\d/,
												/\d/,
												/\d/,
												/\d/,
											]}
										/>
										{errors.phoneNumber !== undefined &&
											touched.phoneNumber && (
												<span className="field-error-message message">
													{errors.phoneNumber}
												</span>
											)}
									</div>
								</div>
							</div>
						</fieldset>

						{!props.isLoggedIn && (
							<fieldset className="fields registration clearfix">
								<div className="container">
									<div className="row">
										<div className="col-sm-12 col-lg-6">
											<h3 className="fields-title col-sm-12 pl-0">
												<span>Registration</span>
												<span
													className="registered"
													onClick={() =>
														props.openModal({
															name: 'authModal',
															properties: {
																authModalView: 'login',
																redirect: 'BOOK',
															},
														})
													}>
													Already have an account?
												</span>
											</h3>
											<div className="fields-wrapper row">
												<div className="field registration-email col-sm-12">
													<input
														type="text"
														name="registrationEmail"
														value={values.registrationEmail}
														onChange={handleChange}
														onFocus={() => setFieldError('registrationEmail')}
														onBlur={() => handleEmailBlur(values)}
														className={`unpakt-textbox ${
															props.emailTaken && 'field-error'
														}
                                ${
																	!props.emailTaken &&
																	touched.registrationEmail &&
																	'fieldSuccessOutline'
																}
                                ${
																	errors.registrationEmail !== undefined &&
																	touched.registrationEmail &&
																	'field-error'
																}`}
														placeholder="Email"
														autoComplete="user-name"
													/>
													{props.emailTaken && (
														<span className="field-error-message message">
															The email {values.registrationEmail} is already
															registered.{' '}
															<span
																className="registered"
																style={{
																	float: 'none',
																	fontFamily: 'ProximaNovaBold',
																	display: 'block',
																}}
																onClick={() =>
																	props.openModal({
																		name: 'authModal',
																		properties: {
																			authModalView: 'login',
																			redirect: 'BOOK',
																		},
																	})
																}>
																Click here to log in.
															</span>
														</span>
													)}
													{errors.registrationEmail &&
														touched.registrationEmail && (
															<span className="field-error-message message">
																{errors.registrationEmail}
															</span>
														)}
												</div>
												<div className="field registration-password col-sm-12 ">
													<input
														type="password"
														name="newPassword1"
														value={values.newPassword1}
														onChange={handleChange}
														onFocus={() => setFieldError('newPassword1')}
														onBlur={handleBlur}
														className={`unpakt-textbox ${
															errors.newPassword1 !== undefined &&
															touched.newPassword1 &&
															touched.newPassword2 &&
															'field-error'
														}`}
														placeholder="Password"
														autoComplete="new-password"
													/>
													{errors.newPassword1 &&
														touched.newPassword1 &&
														touched.newPassword2 && (
															<span className="field-error-message message">
																{errors.newPassword1}
															</span>
														)}
												</div>
												<div className="field registration-password-confirmation col-sm-12">
													<input
														type="password"
														name="newPassword2"
														value={values.newPassword2}
														onChange={handleChange}
														onFocus={() => setFieldError('newPassword2')}
														onBlur={handleBlur}
														className={`unpakt-textbox ${
															errors.newPassword2 !== undefined &&
															touched.newPassword2 &&
															'field-error'
														}`}
														placeholder="Confirm Password"
														autoComplete="new-password"
														required
													/>
													{errors.newPassword2 && touched.newPassword2 && (
														<span className="field-error-message message">
															{errors.newPassword2}
														</span>
													)}
												</div>
											</div>
										</div>
									</div>
								</div>
							</fieldset>
						)}

						<fieldset
							className={`fields billing-info clearfix ${
								paymentType !== 'card' && 'd-none'
							}`}>
							<div className="fields-container billing-name container">
								<h3 className="fields-title col-sm-12 pl-0">Billing</h3>
								<div className="row">
									<div className="field firstname col-sm-12 col-md-6">
										<input
											onChange={handleChange}
											onFocus={() => setFieldError('fname')}
											onBlur={handleBlur}
											type="text"
											name="fname"
											className={`unpakt-textbox ${
												errors.fname !== undefined &&
												touched.fname &&
												'field-error'
											}`}
											placeholder="First name"
											value={values.fname}
											required
										/>
										{errors.fname && touched.fname && (
											<span className="field-error-message message">
												First name is Required
											</span>
										)}
									</div>
									<div className="field lastname col-sm-12 col-md-6">
										<input
											onChange={handleChange}
											onFocus={() => setFieldError('lname')}
											onBlur={handleBlur}
											type="text"
											name="lname"
											className={`unpakt-textbox ${
												errors.lname !== undefined &&
												touched.lname &&
												'field-error'
											}`}
											placeholder="Last name"
											value={values.lname}
											required
										/>
										{errors.lname && touched.lname && (
											<span className="field-error-message message">
												Last name is Required
											</span>
										)}
									</div>
								</div>
								{!values.usingPaypal && (
									<Suspense fallback={''}>
										<AsyncBraintreeCardFields />
									</Suspense>
								)}
							</div>
						</fieldset>

						<div
							id="paypal-container"
							className={`${paymentType !== 'card' && 'd-none'}`}
						/>

						{paymentType === 'paypal' && (
							<div className="container mb-4">
								<div className="row other-payment-applied paypal">
									<div className="col-sm-2 ml-4 font-proxima-bold">Paypal</div>
									<div className="col-sm-8 pl-2">{props.paypalEmail}</div>
									<div
										className="col-sm=2 other-payment-cancel"
										onClick={handlePaypalCancel}>
										Cancel
									</div>
								</div>
							</div>
						)}
						<div
							className={`container pb-3 ${
								paymentType !== 'card' && 'd-none'
							}`}>
							<div className="row">
								<div
									className="col-12 col-md-4 mb-2"
									id="paypal-button"
									onClick={() => setPaymentType('paypal')}
								/>
							</div>
						</div>

						<fieldset className="fields terms container">
							<div className="fields-container row">
								{!props.isBooked && (
									<div className="user-note col-sm-12 col-md-9">
										<label className="font-proxima-bold user-note-label w-100">
											Any Special Requests?
											<textarea
												maxLength="750"
												onChange={handleChange}
												onFocus={() => setFieldError('user_note')}
												onBlur={handleBlur}
												className={`unpakt-textarea w-100 ${
													errors.card_number !== undefined && 'field-error'
												}`}
												value={values.user_note}
												name="user_note"
											/>
										</label>
									</div>
								)}
								<div className="field agree-terms row m-0">
									<div className="unpakt-checkbox-container col-sm-12 mt-3">
										<label>
											<input
												checked={values.agree}
												onChange={handleChange}
												onFocus={() => setFieldError('agree')}
												value={values.agree}
												className={`unpakt-checkbox ${
													errors.card_number !== undefined && 'field-error'
												}`}
												type="checkbox"
												id="agree"
												name="agree"
												required
											/>
											<p>
												I have read, understand and accept the
												<Link to="/agreement" className="terms-conditions link">
													&nbsp;Terms and Conditions
												</Link>
												<br />
												{errors.agree && touched.agree && (
													<span className="field-error-message message">
														You must agree to the terms and conditions
													</span>
												)}
											</p>
										</label>
									</div>
								</div>
							</div>
						</fieldset>

						<div className="fields action-buttons container">
							{errors.length > 0 && (
								<span className="field-error-message message">
									* Please fill in all required fields
								</span>
							)}

							<button
								type="submit"
								disabled={props.loading}
								className="primary-btn-large uppercase book">
								{props.loading ? (
									<ReactLoading
										className="m-auto ladda"
										type={'spokes'}
										color={'#ffffff'}
										height={27}
										width={27}
									/>
								) : props.isBooked ? (
									'Update'
								) : (
									'Book'
								)}
							</button>

							{!props.isBooked && (
								<span className="icon-checkmark font-proxima-bold">
									<a
										className="cancellation-statement"
										href="https://unpakt-faqs.groovehq.com/help/if-i-cancel-my-move-will-i-be-charged">
										✓ &nbsp; Free cancellation up to 2 business days before your
										move
									</a>
								</span>
							)}
							{!props.isLoggedIn && (
								<span
									onClick={() => props.openModal({ name: 'authModal' })}
									className="action-link">
									Save this move plan
								</span>
							)}
						</div>
						<style jsx>{`
							fieldset {
								padding-bottom: 25px;
							}
							.abs-position {
								position: absolute;
								top: -11vh;
								right: -300px;
								max-width: 190px;
								font-size: 19px !important;
							}
							@media (max-width: 767px) {
								.abs-position {
									display: none !important;
								}
							}
							@media (min-width: 768px) and (max-width: 1199px) {
								.abs-position {
									right: -260px;
								}
							}
							@media (min-width: 1200px) and (max-width: 1440px) {
								.abs-position {
									right: -280px;
								}
							}
							@media (min-width: 1441px) and (max-width: 1700px) {
								.abs-position {
									right: -290px;
								}
							}
							.billing-form {
								padding-top: 30px;
							}
							.fields-title {
								font-family: 'ProximaNovaBold', sans-serif;
								font-size: 18px;
							}
							.field-error-message {
								color: red;
								font-size: 0.75rem;
								display: block;
								padding-top: 5px;
								clear: both;
							}
							.field {
								position: relative;
								padding-bottom: 7px;
							}
							.unpakt-textbox {
								margin: 0;
								width: 100%;
							}
							.registered {
								font-size: 11px;
								color: #f7801e;
								text-decoration: underline;
								float: right;
								line-height: 24px;
								cursor: pointer;
							}
							.user-note-label {
								font-size: 18px;
							}
							.unpakt-textarea {
								height: 100px;
								line-height: 18px;
								padding: 10px 14px;
								resize: none;
								display: block;
								margin: 10px 0 0 0;
								width: 60%;
							}
							.unpakt-checkbox-container label {
								line-height: 18px;
								display: inline-block;
								margin-bottom: 0.5rem;
								display: flex;
							}
							.terms-conditions {
								font-size: 13px;
								font-family: 'ProximaNovaBold', sans-serif;
								color: #042b3b;
								border-bottom: 1px solid #d6d6d6;
							}
							.book {
								width: 280px;
								display: inline-block;
								margin: 0 10px 15px 0;
								line-height: 1em;
								height: 45px;
								font-size: 1.6rem;
								font-weight: bold;
								padding: 0 10px;
							}
							.cancellation-statement {
								display: inline-block;
								height: 46px;
								line-height: 46px;
								vertical-align: top;
								font-size: 13px;
								color: #1ecd00;
							}
							.cancellation-statement :hover {
								color: #18a600;
							}
							.action-link,
							.action-buttons > :global(a) {
								display: block;
								margin-bottom: 15px;
								clear: both;
								font-size: 12px;
								text-decoration: underline;
								text-transform: uppercase;
								color: #042b3b;
								cursor: pointer;
							}
							#paypal-button {
								width: 200px;
								height: 35px;
							}
							.other-payment-applied {
								max-width: 800px;
								overflow: hidden;
								padding: 16px;

								border-top: 1px solid rgb(209, 212, 214);
								border-bottom: 1px solid rgb(209, 212, 214);
							}
							.paypal {
								background-image: url(https://checkout.paypal.com/pwpp/2.15.7/images/paypal-small.svg),
									none;
								background-position: 20px 50%;
								background-repeat: no-repeat;
								background-size: 13px 15px;
							}
							.other-payment-cancel {
								cursor: pointer;
								text-decoration: underline;
							}
							.charge-breakdown {
								font-size: 0.85rem;
								line-height: 1.2;
								border-radius: 3px;
							}
							.charge-breakdown-body {
								background: #e0dcfb;
								border-radius: 5px 0 5px 5px;
								font-size: 1rem;
							}
							@media screen and (max-width: 400px) {
								.charge-breakdown,
								.charge-breakdown-body {
									font-size: 0.7rem;
								}
							}
							.charge-breakdown-body .col-4 {
								border-top: 1px solid #c1bbee;
								color: #423c66;
							}
							.important-note {
								color: red;
								line-height: 1;
								font-size: 0.8rem;
							}
							.top-row {
								border-top: none !important;
							}
							.primary-btn-large {
								min-height: 40px;
							}
						`}</style>
					</form>
				)}
			/>
		</>
	);
};

export default connect(mapStateToProps, {
	openModal,
	checkEmailAvailability,
	registerUser,
	setBookContact,
	updateMovePlan,
	book,
	cancelPaypal,
	setCardErrors,
	setEmailTaken,
})(BookForm);
